/**
 * Renew Plan Page
 *
 * Fetches the current plan's terms and lets the user select one to renew.
 * Same layout as the Upgrade Plan "terms" view.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CreditCard,
  Shield,
  Users,
  FileText,
  Briefcase,
} from 'lucide-react';

import {
  Button,
  Badge,
  Card,
  CardBody,
  Loader,
  Modal,
} from '@/components/common';
import { subscriptionService, organizationService } from '@/api/services';
import type { PlanTerm } from '@/api/services/subscription.service';
import type { OrganizationPlan } from '@/api/types';
import { useToast } from '@/hooks/useToast';

export function RenewPlanPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [plan, setPlan] = useState<OrganizationPlan | null>(null);
  const [terms, setTerms] = useState<PlanTerm[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [confirmTerm, setConfirmTerm] = useState<PlanTerm | null>(null);
  const [isRenewing, setIsRenewing] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const planData = id
          ? await organizationService.getOrganizationPlan(id)
          : [];
        const currentPlan = planData[0] || null;
        setPlan(currentPlan);

        if (currentPlan?.currentPlanId) {
          const planTerms = await subscriptionService.getPlanTermsByPlanId(
            currentPlan.currentPlanId
          );
          setTerms(Array.isArray(planTerms) ? planTerms : []);
        }
      } catch (error) {
        console.error('Failed to fetch plan terms:', error);
        toast.error('Failed to load plan terms');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleRenew = async () => {
    if (!confirmTerm || !id || !plan?.subscriptionPlanCode) return;
    setIsRenewing(true);
    try {
      await subscriptionService.upgradeSubscriptionPlan({
        orgId: id,
        subscriptionPlanCode: plan.subscriptionPlanCode,
        planTermCode: confirmTerm.planTermCode,
      });
      toast.success('Plan renewed successfully');
      setConfirmTerm(null);
      navigate(`/organizations/${id}`);
    } catch (error) {
      console.error('Failed to renew plan:', error);
      toast.error('Failed to renew plan');
    } finally {
      setIsRenewing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader size="lg" message="Loading plan terms..." />
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/organizations/${id}`)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold text-foreground dark:text-foreground-dark">
            Renew Plan
          </h1>
        </div>
        <div className="py-12 text-center">
          <CreditCard className="mx-auto h-12 w-12 text-foreground-muted dark:text-foreground-dark-muted" />
          <p className="mt-2 text-foreground-muted dark:text-foreground-dark-muted">
            No subscription plan found for this organization
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(`/organizations/${id}`)}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground dark:text-foreground-dark">
            {plan.name}
          </h1>
          <p className="mt-0.5 text-sm text-foreground-muted dark:text-foreground-dark-muted">
            Select a term to renew
          </p>
        </div>
      </div>

      {/* Plan details summary */}
      <Card>
        <CardBody>
          <div className="flex items-center gap-4 mb-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
              <Shield className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground dark:text-foreground-dark">{plan.name}</h2>
              <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted">{plan.planTermType}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <div className="flex items-center gap-2 text-sm">
              <Users className="h-4 w-4 text-foreground-muted dark:text-foreground-dark-muted" />
              <span className="text-foreground-muted dark:text-foreground-dark-muted">Employees:</span>
              <span className="font-semibold text-foreground dark:text-foreground-dark">{plan.maxEmployees}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <FileText className="h-4 w-4 text-foreground-muted dark:text-foreground-dark-muted" />
              <span className="text-foreground-muted dark:text-foreground-dark-muted">Contracts:</span>
              <span className="font-semibold text-foreground dark:text-foreground-dark">{plan.maxContracts}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Briefcase className="h-4 w-4 text-foreground-muted dark:text-foreground-dark-muted" />
              <span className="text-foreground-muted dark:text-foreground-dark-muted">Active:</span>
              <span className="font-semibold text-foreground dark:text-foreground-dark">{plan.maxActiveContracts}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Users className="h-4 w-4 text-foreground-muted dark:text-foreground-dark-muted" />
              <span className="text-foreground-muted dark:text-foreground-dark-muted">Users:</span>
              <span className="font-semibold text-foreground dark:text-foreground-dark">{plan.maxUsers ?? 'N/A'}</span>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Plan Terms */}
      {terms.length === 0 ? (
        <div className="py-8 text-center">
          <CreditCard className="mx-auto h-12 w-12 text-foreground-muted dark:text-foreground-dark-muted" />
          <p className="mt-2 text-foreground-muted dark:text-foreground-dark-muted">
            No terms available for this plan
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {terms.map((term) => (
            <Card key={term.id}>
              <CardBody className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-bold text-foreground dark:text-foreground-dark">
                    {term.termType}
                  </h3>
                  {term.isActive && (
                    <Badge variant="success" dot>Active</Badge>
                  )}
                </div>
                <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted mb-4">
                  Duration: {term.termValue} {term.termType.toLowerCase()}
                </p>
                <div className="space-y-2">
                  {term.actualPrice > term.price && (
                    <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted line-through">
                      ${term.actualPrice}
                    </p>
                  )}
                  <p className="text-3xl font-bold text-foreground dark:text-foreground-dark">
                    ${term.price}
                  </p>
                </div>
                <Button
                  variant="primary"
                  className="mt-4 w-full"
                  disabled={!term.isActive}
                  onClick={() => setConfirmTerm(term)}
                >
                  Select Term
                </Button>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {/* Renew Confirmation Modal */}
      <Modal
        open={!!confirmTerm}
        onOpenChange={(open) => !open && setConfirmTerm(null)}
        title="Confirm Renewal"
        description="Please review the details before proceeding."
        size="sm"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setConfirmTerm(null)}
              disabled={isRenewing}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleRenew}
              disabled={isRenewing}
            >
              {isRenewing ? 'Renewing...' : 'Yes, Renew'}
            </Button>
          </>
        }
      >
        {confirmTerm && (
          <div className="space-y-3">
            <div className="rounded-lg bg-primary/5 p-4">
              <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted">Plan</p>
              <p className="text-lg font-semibold text-foreground dark:text-foreground-dark">{plan.name}</p>
            </div>
            <div className="rounded-lg bg-primary/5 p-4">
              <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted">Term</p>
              <p className="text-lg font-semibold text-foreground dark:text-foreground-dark">
                {confirmTerm.termType} &mdash; {confirmTerm.termValue} {confirmTerm.termType.toLowerCase()}
              </p>
            </div>
            <div className="rounded-lg bg-primary/5 p-4">
              <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted">Price</p>
              <p className="text-lg font-semibold text-foreground dark:text-foreground-dark">${confirmTerm.price}</p>
            </div>
            <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted">
              Are you sure you want to renew this plan? This action will take effect immediately.
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default RenewPlanPage;
